import React, { useState, useEffect } from 'react';
import { ClubEvent, ConventionSession, ConventionScheduleItem, UserProfile } from '../types';
import { db, handleFirestoreError, OperationType } from '../lib/firebase';
import { collection, addDoc, updateDoc, doc, getDocs, query, where, deleteDoc } from 'firebase/firestore';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { EVENT_TYPES } from '../constants';
import { cleanIcsString, safeUserSearch } from '../lib/utils';
import { toast } from 'sonner';
import { Plus, Trash2, Users, Search } from 'lucide-react';

interface EventFormProps {
  isOpen: boolean;
  onClose: () => void;
  event: ClubEvent | null;
  users?: UserProfile[];
}

export default function EventForm({ isOpen, onClose, event, users = [] }: EventFormProps) {
  const [formData, setFormData] = useState<Partial<ClubEvent>>({
    name: '',
    startDate: '',
    endDate: '',
    location: '',
    host: '',
    contact: '',
    points: 10,
    milesTraveled: 0,
    type: 'Meeting',
    status: 'active'
  });

  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());
  const [initialAttendanceMap, setInitialAttendanceMap] = useState<Map<string, string>>(new Map()); // uid -> attendanceId
  const [attendeeSearch, setAttendeeSearch] = useState('');

  useEffect(() => {
    setAttendeeSearch('');
    if (event) {
      setFormData({
        ...event,
        name: cleanIcsString(event.name),
        location: cleanIcsString(event.location),
        pertinentInfo: cleanIcsString(event.pertinentInfo),
        status: event.status || 'active'
      });

      // Load existing attendance for this event
      if (event.id) {
        const loadAttendance = async () => {
          try {
            const q = query(
              collection(db, 'attendance'),
              where('eventId', '==', event.id),
              where('status', '==', 'attending')
            );
            const snap = await getDocs(q);
            const map = new Map<string, string>();
            const uids = new Set<string>();
            snap.docs.forEach(d => {
              const data = d.data();
              if (data.userId) {
                map.set(data.userId, d.id);
                uids.add(data.userId);
              }
            });
            setInitialAttendanceMap(map);
            setSelectedUserIds(uids);
          } catch (e) {
            console.error('Failed to load event attendance', e);
          }
        };
        loadAttendance();
      } else {
        setSelectedUserIds(new Set());
        setInitialAttendanceMap(new Map());
      }
    } else {
      setFormData({
        name: '',
        startDate: '',
        endDate: '',
        location: '',
        host: '',
        contact: '',
        points: 10,
        milesTraveled: 0,
        type: 'Meeting',
        status: 'active'
      });
      setSelectedUserIds(new Set());
      setInitialAttendanceMap(new Map());
    }
  }, [event, isOpen]);

  const toggleUserSelection = (uid: string) => {
    const next = new Set(selectedUserIds);
    if (next.has(uid)) {
      next.delete(uid);
    } else {
      next.add(uid);
    }
    setSelectedUserIds(next);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const dbData = Object.fromEntries(Object.entries(formData).filter(([k, v]) => v !== undefined && k !== 'id'));
      let eventId = event?.id;
      if (eventId) {
        await updateDoc(doc(db, 'events', eventId), dbData);
        toast.success('Event updated');
      } else {
        const docRef = await addDoc(collection(db, 'events'), dbData);
        eventId = docRef.id;
        toast.success('Event created');
      }

      // Sync attendance records if users prop is provided
      if (eventId && users.length > 0) {
        // Add new attendees
        const toAdd = Array.from(selectedUserIds).filter(uid => !initialAttendanceMap.has(uid));
        const addPromises = toAdd.map(uid => 
          addDoc(collection(db, 'attendance'), {
            userId: uid,
            eventId,
            status: 'attending',
            timestamp: new Date().toISOString()
          })
        );

        // Remove unselected attendees
        const toRemove = Array.from(initialAttendanceMap.entries()).filter(([uid]) => !selectedUserIds.has(uid));
        const removePromises = toRemove.map(([, attendanceId]) => 
          deleteDoc(doc(db, 'attendance', attendanceId))
        );

        await Promise.all([...addPromises, ...removePromises]);
        if (toAdd.length > 0) {
          toast.success(`Awarded ${formData.points || 10} points to ${toAdd.length} attendee(s).`);
        }
      }

      onClose();
    } catch (error) {
      handleFirestoreError(error, OperationType.WRITE, 'events');
    }
  };

  const addSession = () => {
    const newSession: ConventionSession = {
      id: Math.random().toString(36).substr(2, 9),
      name: '',
      startTime: '',
      endTime: '',
    };
    setFormData({ ...formData, sessions: [...(formData.sessions || []), newSession] });
  };

  const updateSession = (id: string, field: keyof ConventionSession, value: string) => {
    setFormData({
      ...formData,
      sessions: formData.sessions?.map(s => s.id === id ? { ...s, [field]: value } : s)
    });
  };

  const removeSession = (id: string) => {
    setFormData({
      ...formData,
      sessions: formData.sessions?.filter(s => s.id !== id)
    });
  };

  const addScheduleItem = () => {
    const newItem: ConventionScheduleItem = {
      id: Math.random().toString(36).substr(2, 9),
      time: '',
      activity: '',
    };
    setFormData({ ...formData, schedule: [...(formData.schedule || []), newItem] });
  };

  const updateScheduleItem = (id: string, field: keyof ConventionScheduleItem, value: string) => {
    setFormData({
      ...formData,
      schedule: formData.schedule?.map(s => s.id === id ? { ...s, [field]: value } : s)
    });
  };

  const removeScheduleItem = (id: string) => {
    setFormData({
      ...formData,
      schedule: formData.schedule?.filter(s => s.id !== id)
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-card-bg border border-border-color text-[var(--foreground)] max-w-3xl shadow-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader className="border-b border-border-color pb-4">
          <DialogTitle className="uppercase text-accent-gold tracking-widest font-bold">
            {event ? 'Edit Mission Log' : 'New Mission Assignment'}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-5 py-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="md:col-span-2 space-y-1.5">
              <Label htmlFor="name" className="text-[0.7rem] uppercase font-bold text-text-secondary tracking-wider">Mission Name</Label>
              <Input 
                id="name" 
                value={formData.name} 
                onChange={e => setFormData({ ...formData, name: e.target.value })} 
                className="bg-[#0d1117] border-border-color focus:border-accent-blue text-sm h-10"
                required
              />
            </div>
            
            <div className="space-y-1.5">
              <Label htmlFor="type" className="text-[0.7rem] uppercase font-bold text-text-secondary tracking-wider">Mission Type</Label>
              <Select 
                value={formData.type} 
                onValueChange={val => setFormData({ ...formData, type: val })}
              >
                <SelectTrigger className="bg-[#0d1117] border-border-color focus:border-accent-blue h-10">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent className="bg-card-bg border-border-color text-[var(--foreground)]">
                  {EVENT_TYPES.map(t => (
                    <SelectItem key={t} value={t} className="focus:bg-accent-blue focus:text-[var(--foreground)]">{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="status" className="text-[0.7rem] uppercase font-bold text-text-secondary tracking-wider">Mission Status</Label>
              <Select 
                value={formData.status} 
                onValueChange={val => setFormData({ ...formData, status: val as any })}
              >
                <SelectTrigger className="bg-[#0d1117] border-border-color focus:border-accent-blue h-10">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent className="bg-card-bg border-border-color text-[var(--foreground)]">
                  <SelectItem value="draft" className="focus:bg-accent-blue focus:text-[var(--foreground)]">DRAFT (PLANNING)</SelectItem>
                  <SelectItem value="active" className="focus:bg-accent-blue focus:text-[var(--foreground)]">ACTIVE (ONGOING)</SelectItem>
                  <SelectItem value="completed" className="focus:bg-accent-blue focus:text-[var(--foreground)]">COMPLETED (ARCHIVED)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="points" className="text-[0.7rem] uppercase font-bold text-text-secondary tracking-wider">Points Awarded</Label>
              <Input 
                id="points" 
                type="number"
                value={formData.points} 
                onChange={e => setFormData({ ...formData, points: parseInt(e.target.value) || 0 })} 
                className="bg-[#0d1117] border-border-color focus:border-accent-blue h-10"
                required
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="startDate" className="text-[0.7rem] uppercase font-bold text-text-secondary tracking-wider">Start Stardate</Label>
              <Input 
                id="startDate" 
                type="datetime-local"
                value={formData.startDate || ''} 
                onChange={e => setFormData({ ...formData, startDate: e.target.value })} 
                className="bg-[#0d1117] border-border-color focus:border-accent-blue h-10"
                required
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="endDate" className="text-[0.7rem] uppercase font-bold text-text-secondary tracking-wider">End Stardate (Optional)</Label>
              <Input 
                id="endDate" 
                type="datetime-local"
                value={formData.endDate || ''} 
                onChange={e => setFormData({ ...formData, endDate: e.target.value })} 
                className="bg-[#0d1117] border-border-color focus:border-accent-blue h-10"
              />
            </div>

            <div className="md:col-span-2 space-y-1.5">
              <Label htmlFor="location" className="text-[0.7rem] uppercase font-bold text-text-secondary tracking-wider">Location Coordinates</Label>
              <Input 
                id="location" 
                value={formData.location || ''} 
                onChange={e => setFormData({ ...formData, location: e.target.value })} 
                className="bg-[#0d1117] border-border-color focus:border-accent-blue h-10"
                placeholder="123 Starfleet Way, San Francisco, CA"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="host" className="text-[0.7rem] uppercase font-bold text-text-secondary tracking-wider">Mission Host</Label>
              <Input 
                id="host" 
                value={formData.host || ''} 
                onChange={e => setFormData({ ...formData, host: e.target.value })} 
                className="bg-[#0d1117] border-border-color focus:border-accent-blue h-10"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="contact" className="text-[0.7rem] uppercase font-bold text-text-secondary tracking-wider">Comm Frequency (Contact)</Label>
              <Input 
                id="contact" 
                value={formData.contact || ''} 
                onChange={e => setFormData({ ...formData, contact: e.target.value })} 
                className="bg-[#0d1117] border-border-color focus:border-accent-blue h-10"
              />
            </div>

            <div className="md:col-span-2 space-y-1.5">
              <Label htmlFor="summary" className="text-[0.7rem] uppercase font-bold text-lcars-blue tracking-wider">Mission Summary / After-Action Report</Label>
              <Textarea 
                id="summary" 
                value={formData.summary || ''} 
                onChange={e => setFormData({ ...formData, summary: e.target.value })} 
                className="bg-[#0d1117] border-border-color focus:border-accent-blue min-h-[80px]"
                placeholder="Provide an overview and debrief of what took place during this mission..."
              />
            </div>

            <div className="md:col-span-2 space-y-1.5">
              <Label htmlFor="lessonsLearned" className="text-[0.7rem] uppercase font-bold text-lcars-gold tracking-wider">Tactical Lessons Learned</Label>
              <Textarea 
                id="lessonsLearned" 
                value={formData.lessonsLearned || ''} 
                onChange={e => setFormData({ ...formData, lessonsLearned: e.target.value })} 
                className="bg-[#0d1117] border-border-color focus:border-accent-blue min-h-[70px]"
                placeholder="Key takeaways, improvements for next time, and operational observations..."
              />
            </div>

            {formData.type === 'Convention' && (
              <div className="md:col-span-2 p-4 bg-lcars-blue/10 border border-lcars-blue rounded text-center">
                <p className="text-lcars-blue font-bold uppercase text-xs tracking-tighter">
                  Tactical Notice: Convention logistics and scheduling are managed via the CON DUTY ROSTER interface.
                </p>
                <p className="text-[0.6rem] text-[var(--foreground)]/60 mt-1 uppercase tracking-widest">
                  Modify scenes, host data, and schedules in the dedicated Con Duty Roster tab.
                </p>
              </div>
            )}

            {/* Attendees Assignment & Point Awarding */}
            {users.length > 0 && (
              <div className="md:col-span-2 space-y-3 pt-3 border-t border-white/10">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-lcars-gold" />
                    <Label className="text-xs uppercase font-bold text-lcars-gold tracking-widest">
                      Mission Personnel Attendance ({selectedUserIds.size} Selected • +{formData.points || 10} PTS each)
                    </Label>
                  </div>
                  <span className="text-[0.65rem] text-[var(--foreground)]/50 font-mono">
                    Check members to record attendance
                  </span>
                </div>

                <div className="relative">
                  <Search className="w-3.5 h-3.5 absolute left-3 top-2.5 text-[var(--foreground)]/40" />
                  <Input
                    value={attendeeSearch}
                    onChange={e => setAttendeeSearch(e.target.value)}
                    placeholder="Search crew members..."
                    className="pl-8 bg-[#0d1117] border-border-color h-8 text-xs font-sans"
                  />
                </div>

                <div className="max-h-48 overflow-y-auto divide-y divide-white/5 border border-white/10 rounded bg-black/40 p-2">
                  {users
                    .filter(u => safeUserSearch(u, attendeeSearch))
                    .map(u => {
                      const isChecked = selectedUserIds.has(u.uid);
                      return (
                        <label 
                          key={u.uid}
                          className="flex items-center justify-between p-2 hover:bg-white/5 rounded cursor-pointer transition-colors"
                        >
                          <div className="flex items-center gap-2.5">
                            <Checkbox
                              checked={isChecked}
                              onCheckedChange={() => toggleUserSelection(u.uid)}
                            />
                            <div>
                              <span className="text-xs font-bold font-sans">
                                {u.firstName} {u.lastName}
                              </span>
                              <span className="text-[0.65rem] text-[var(--foreground)]/40 ml-2 font-mono">
                                {u.email}
                              </span>
                            </div>
                          </div>
                          {isChecked && (
                            <span className="text-[0.65rem] bg-lcars-gold/20 text-lcars-gold px-2 py-0.5 rounded font-mono font-bold">
                              ATTENDING
                            </span>
                          )}
                        </label>
                      );
                    })}
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="pt-6 border-t border-border-color flex flex-col sm:flex-row gap-3">
            <button 
              type="button" 
              onClick={onClose}
              className="lcars-pill bg-lcars-tan text-black px-6 py-2 text-[0.75rem] font-bold uppercase tracking-widest"
            >
              Aborted
            </button>
            <button 
              type="submit" 
              className="lcars-pill bg-lcars-blue text-black px-8 py-2 text-[0.75rem] font-bold uppercase tracking-widest"
            >
              {event ? 'Update Mission' : 'Initiate Mission'}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
